import type { GetMessagesQueryVariables } from '#src/graphql/generated';
import type { PendingMessageAttachment } from '#src/pages/document/document-chat-composer-context';

import { useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  IconButton,
  Text,
  VStack,
  Wrap,
} from '@chakra-ui/react';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { FiPaperclip, FiX } from 'react-icons/fi';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { MessageList } from '#src/components/message-list';
import { toaster } from '#src/components/ui/toaster';
import { MESSAGE_ATTACHMENT_UPLOAD_IDS } from '#src/constants/message-context-names';
import { buildGetMessagesVariables } from '#src/graphql/build-get-messages-variables';
import { mergeCreateMessageIntoGetMessagesCache } from '#src/graphql/cache/merge-create-message-into-get-messages';
import { CREATE_MESSAGE } from '#src/graphql/mutations/create-message';
import { DELETE_UPLOAD } from '#src/graphql/mutations/delete-upload';
import { useMessagesQueryPanel } from '#src/hooks/use-messages-query-panel';
import { useScrollMessageListToEnd } from '#src/hooks/use-scroll-message-list-to-end';
import {
  composerAttachmentMarkdownLine,
  uploadFileForMessageDraft,
} from '#src/pages/document/upload-image';
import { useAppConfig } from '#src/providers/app-config-provider';
import { usePageFooter } from '#src/providers/page-layout-provider';
import { useCurrentUser } from '#src/providers/user-provider';

type DashboardMessagesScope = 'filtered' | 'all';

export function Dashboard() {
  const { footerRef } = usePageFooter();
  const { config } = useAppConfig();
  const apiHost = config.apiHost;
  const user = useCurrentUser();

  const [messagesScope, setMessagesScope] =
    useState<DashboardMessagesScope>('filtered');

  const getMessagesVariables = useMemo<GetMessagesQueryVariables>(
    () =>
      messagesScope === 'filtered'
        ? buildGetMessagesVariables({ kind: 'dashboard-filtered' })
        : buildGetMessagesVariables({ kind: 'dashboard-all' }),
    [messagesScope],
  );

  const [messageText, setMessageText] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingMessageAttachment[]
  >([]);
  const [attachmentUploadBusy, setAttachmentUploadBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [createMessage, { loading: isSendingMessage }] =
    useMutation(CREATE_MESSAGE);
  const [deleteUpload] = useMutation(DELETE_UPLOAD);

  const {
    displayMessages,
    focusedRootId,
    isMessagesInitialLoad,
    messagesError,
    refetchMessages,
    expandThread,
    collapseThread,
  } = useMessagesQueryPanel({
    variables: getMessagesVariables,
    skip: false,
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  useScrollMessageListToEnd(messagesEndRef, displayMessages);

  const hasSendableContent =
    messageText.trim().length > 0 || pendingAttachments.length > 0;

  const handleSendMessage = async () => {
    if (!hasSendableContent) return;
    const trimmed = messageText.trim();
    const bodyParts: string[] = [];
    if (trimmed.length > 0) {
      bodyParts.push(trimmed);
    }
    for (const att of pendingAttachments) {
      bodyParts.push(
        composerAttachmentMarkdownLine(
          att.filename,
          att.id,
          att.url,
          att.isEncrypted,
          apiHost,
        ),
      );
    }
    const value = bodyParts.join('\n\n');
    const hadNoThreadFocus = !focusedRootId;
    try {
      const contexts =
        pendingAttachments.length > 0
          ? [
              {
                name: MESSAGE_ATTACHMENT_UPLOAD_IDS,
                value: JSON.stringify(pendingAttachments.map((a) => a.id)),
              },
            ]
          : undefined;

      const { data } = await createMessage({
        variables: {
          input: {
            value,
            parentMessageId: focusedRootId ?? undefined,
            ...(contexts ? { contexts } : {}),
          },
        },
        update(cache, { data: mutationData }) {
          if (mutationData?.createMessage) {
            mergeCreateMessageIntoGetMessagesCache(
              cache,
              mutationData.createMessage,
              getMessagesVariables,
            );
          }
        },
      });
      setMessageText('');
      setPendingAttachments([]);
      if (hadNoThreadFocus && data?.createMessage?.id) {
        expandThread(data.createMessage.id);
      }
    } catch (error) {
      toaster.create({
        title: 'Failed to send message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const onAttachmentFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      try {
        setAttachmentUploadBusy(true);
        const result = await uploadFileForMessageDraft(file, { apiHost });
        setPendingAttachments((prev) => [
          ...prev,
          {
            id: result.id,
            filename: file.name,
            url: result.url,
            isEncrypted: result.isEncrypted,
          },
        ]);
      } catch (error) {
        toaster.create({
          title: 'Upload failed',
          description: error instanceof Error ? error.message : 'Unknown error',
          type: 'error',
        });
      } finally {
        setAttachmentUploadBusy(false);
      }
    },
    [apiHost],
  );

  const removeDraftAttachment = useCallback(
    async (id: string) => {
      try {
        await deleteUpload({ variables: { id } });
      } catch (error) {
        toaster.create({
          title: 'Could not remove file',
          description: error instanceof Error ? error.message : 'Unknown error',
          type: 'error',
        });
        return;
      }
      setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
    },
    [deleteUpload],
  );

  const footerContent = user.email ? (
    <VStack gap={2} align="stretch" width="100%">
      {focusedRootId && (
        <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
          <Text>Replying in thread</Text>
          <Button size="xs" variant="ghost" onClick={() => collapseThread()}>
            Cancel
          </Button>
        </HStack>
      )}
      {pendingAttachments.length > 0 ? (
        <Wrap gap={1}>
          {pendingAttachments.map((att) => (
            <HStack
              key={att.id}
              gap={1}
              px={2}
              py={1}
              borderRadius="md"
              bg="bg.subtle"
              fontSize="xs"
              maxW="100%"
            >
              <Text truncate title={att.filename}>
                {att.filename}
              </Text>
              <IconButton
                aria-label={`Remove ${att.filename}`}
                size="xs"
                variant="ghost"
                onClick={() => void removeDraftAttachment(att.id)}
              >
                <FiX />
              </IconButton>
            </HStack>
          ))}
        </Wrap>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={(ev) => void onAttachmentFileChange(ev)}
      />
      <ChatComposerRow
        value={messageText}
        onChange={setMessageText}
        onSubmit={() => void handleSendMessage()}
        placeholder={focusedRootId ? 'Type a reply…' : 'Type a message…'}
        loading={isSendingMessage}
        disabled={attachmentUploadBusy}
        autoGrow
        submitDisabled={!hasSendableContent}
        trailingActions={
          <IconButton
            type="button"
            aria-label="Attach file"
            title="Attach file"
            size="sm"
            variant="outline"
            loading={attachmentUploadBusy}
            disabled={isSendingMessage}
            onClick={() => fileInputRef.current?.click()}
          >
            <FiPaperclip />
          </IconButton>
        }
      />
    </VStack>
  ) : null;

  return (
    <Box p={4}>
      {/* Render message input into footer portal */}
      {footerRef.current && createPortal(footerContent, footerRef.current)}

      <HStack justify="flex-end" align="center" gap={2} mb={2}>
        <Text fontSize="sm" color="fg.muted">
          Messages
        </Text>
        <ButtonGroup size="xs" variant="outline" attached>
          <Button
            variant={messagesScope === 'filtered' ? 'solid' : 'outline'}
            onClick={() => setMessagesScope('filtered')}
          >
            Chat
          </Button>
          <Button
            variant={messagesScope === 'all' ? 'solid' : 'outline'}
            onClick={() => setMessagesScope('all')}
          >
            All
          </Button>
        </ButtonGroup>
      </HStack>

      <MessageList
        messages={displayMessages}
        loading={isMessagesInitialLoad}
        error={messagesError ?? undefined}
        emptyMessage="No messages found."
        focusedRootId={focusedRootId}
        onExpandThread={expandThread}
        messagesEndRef={messagesEndRef}
        alignMessagesToBottom
        onMessageDeleted={() => void refetchMessages()}
      />
    </Box>
  );
}
